import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useCurrentOperator } from 'Src/feature/operator/useCurrentOperator';
import { ServiceError, useError } from 'Src/network/useError';
import { definitions } from 'Src/vendor/posapi/pos';

import type { CustomerDetails, CustomerSearch, CustomerSearchResult, LocationProgramStatus, UATCustomerEnrollment } from './loyaltyService';
import service from './loyaltyService';

const queryKeys = {
  getDetails: (id: number) => ['POS:GET:Customer.Details', id],
  search: (customerSearchString: string) => ['POS:GET:Customers', customerSearchString],
  getLocationProgramEnrollment: () => ['POS:GET:Loyalty.ProgramStatus'],
};


const useQuery_SearchCustomers = <TData = CustomerSearchResult>(query: CustomerSearch, select?: (results: CustomerSearchResult) => TData) => {
  const { t } = useTranslation();
  const { parseError } = useError({ activity: t('loyalty.errors.searchCustomers') });
  return useQuery<CustomerSearchResult, ServiceError, TData>({
    queryKey: queryKeys.search(JSON.stringify(query)),
    staleTime: 0, // invalidate cache immediately to refetch as this is a real-time search
    queryFn: async () => {
      try {
        return await service.search({ queryOpts: query.toServer() as definitions['query.Opts'] }) as CustomerSearchResult;
      } catch (e) {
        throw parseError(e);
      }
    },
    select,
  });
};

const useQuery_GetDetails = <TData = CustomerDetails>(customerId: number, select?: (results: CustomerDetails) => TData) => {
  const { t } = useTranslation();
  const { parseError } = useError({ activity: t('loyalty.errors.getCustomerDetails') });
  return useQuery<CustomerDetails, ServiceError, TData>({
    queryKey: queryKeys.getDetails(customerId),
    queryFn: async () => {
      try {
        return await service.getDetails({ customerId });
      } catch (e) {
        throw parseError(e);
      }
    },
    select,
  });
};

const useQuery_GetLocationEnrollment = <TData = LocationProgramStatus>(select?: (results: LocationProgramStatus) => TData) => {
  const { t } = useTranslation();
  const { parseError } = useError({ activity: t('loyalty.errors.getProgramEnrollment') });
  const { currentOperator } = useCurrentOperator();
  return useQuery<LocationProgramStatus, ServiceError, TData>({
    queryKey: queryKeys.getLocationProgramEnrollment(),
    queryFn: async () => {
      try {
        return await service.getLocationProgramEnrollment();
      } catch (e) {
        throw parseError(e);
      }
    },
    select,
    // block usage of this hook until we have a current operator available
    // - resolves the error we see when trying to run this hook automatically via the react-query run when first mounting
    enabled: !!currentOperator,
  });
};

const useMutate_DevToolCustomerEnroll = (onError?: (error: ServiceError) => void) => {
  const { t } = useTranslation();
  const { parseError } = useError({ activity: t('devTool.loyaltyCustomerOnboard.errors.enroll') });
  // I've no idea if this is right....
  return useMutation({
    mutationFn: async (c: UATCustomerEnrollment) => {
      try {
        await service.devTools.enrollCustomer({ customer: c });
        return;
      } catch (e) {
        throw parseError(e);
      }
    },
    onError,
  });
};

const useLoyalty = {
  query: {
    customers: {
      search: useQuery_SearchCustomers,
      getDetails: useQuery_GetDetails,
    },
    program: {
      getEnrollmentStatus: useQuery_GetLocationEnrollment,
    },
  },
  mutate: {
    customers: {
      devTool: {
        enrollCustomer: useMutate_DevToolCustomerEnroll,
      },
    },
  },
};

export { useLoyalty };
